import React from "react";

const MicroclimateAnalysis = ({ data, dataType }) => {
  if (!data || !data.data || !data.data.length) {
    return null;
  }

  // Determine if we're analyzing temperature or humidity
  const isTemperature = dataType === "temperature";
  const unit = isTemperature ? "°C" : "%";
  const variableName = isTemperature ? "temperature" : "humidity";

  // Calculate microclimate statistics
  const calculateStats = () => {
    const dailyData = data.data;

    // Find extreme values
    const maxValue = Math.max(...dailyData.map((day) => day.max));
    const minValue = Math.min(...dailyData.map((day) => day.min));

    // Count days above/below thresholds
    let hotDays = 0;
    let coldDays = 0;
    let comfortableDays = 0;
    let extremelyHotDays = 0;
    let extremelyColdDays = 0;

    if (isTemperature) {
      // Temperature thresholds
      hotDays = dailyData.filter((day) => day.max > 30).length;
      extremelyHotDays = dailyData.filter((day) => day.max > 35).length;
      coldDays = dailyData.filter((day) => day.min < 5).length;
      extremelyColdDays = dailyData.filter((day) => day.min < 0).length;
      comfortableDays = dailyData.filter(
        (day) =>
          day.max >= 20 && day.max <= 25 && day.min >= 15 && day.min <= 20
      ).length;
    } else {
      // Humidity thresholds
      hotDays = dailyData.filter((day) => day.mean > 80).length; // High humidity days
      extremelyHotDays = dailyData.filter((day) => day.mean > 90).length; // Very high humidity
      coldDays = dailyData.filter((day) => day.mean < 30).length; // Low humidity days
      extremelyColdDays = dailyData.filter((day) => day.mean < 20).length; // Very low humidity
      comfortableDays = dailyData.filter(
        (day) => day.mean >= 40 && day.mean <= 60
      ).length; // Comfortable humidity
    }

    // Calculate seasonal averages
    const seasons = {
      winter: { months: [12, 1, 2], data: [] },
      spring: { months: [3, 4, 5], data: [] },
      summer: { months: [6, 7, 8], data: [] },
      fall: { months: [9, 10, 11], data: [] },
    };

    dailyData.forEach((day) => {
      Object.keys(seasons).forEach((season) => {
        if (seasons[season].months.includes(day.month)) {
          seasons[season].data.push(day);
        }
      });
    });

    // Calculate average for each season
    Object.keys(seasons).forEach((season) => {
      const seasonData = seasons[season].data;
      if (seasonData.length > 0) {
        const avgMin =
          seasonData.reduce((sum, day) => sum + day.min, 0) / seasonData.length;
        const avgMax =
          seasonData.reduce((sum, day) => sum + day.max, 0) / seasonData.length;
        const avgMean =
          seasonData.reduce((sum, day) => sum + day.mean, 0) /
          seasonData.length;

        seasons[season].avgMin = avgMin.toFixed(1);
        seasons[season].avgMax = avgMax.toFixed(1);
        seasons[season].avgMean = avgMean.toFixed(1);
      }
    });

    // Calculate diurnal range (average daily temperature swing)
    const diurnalRange =
      dailyData.reduce((sum, day) => sum + (day.max - day.min), 0) /
      dailyData.length;

    return {
      maxValue: maxValue.toFixed(1),
      minValue: minValue.toFixed(1),
      hotDays,
      coldDays,
      comfortableDays,
      extremelyHotDays,
      extremelyColdDays,
      seasons,
      diurnalRange: diurnalRange.toFixed(1),
    };
  };

  const stats = calculateStats();

  return (
    <div className="microclimate-analysis">
      <h3>Microclimate Analysis</h3>

      <div className="microclimate-grid">
        <div className="microclimate-section">
          <h4>Extreme Values</h4>
          <ul>
            <li>
              Maximum {variableName}:{" "}
              <strong>
                {stats.maxValue}
                {unit}
              </strong>
            </li>
            <li>
              Minimum {variableName}:{" "}
              <strong>
                {stats.minValue}
                {unit}
              </strong>
            </li>
            {isTemperature && (
              <li>
                Average daily temperature range:{" "}
                <strong>
                  {stats.diurnalRange}
                  {unit}
                </strong>
              </li>
            )}
          </ul>
        </div>

        <div className="microclimate-section">
          <h4>Day Count Analysis</h4>
          <ul>
            {isTemperature ? (
              <>
                <li>
                  Hot days (&gt;30°C): <strong>{stats.hotDays} days</strong>
                </li>
                <li>
                  Extremely hot days (&gt;35°C):{" "}
                  <strong>{stats.extremelyHotDays} days</strong>
                </li>
                <li>
                  Cold days (&lt;5°C): <strong>{stats.coldDays} days</strong>
                </li>
                <li>
                  Freezing days (&lt;0°C):{" "}
                  <strong>{stats.extremelyColdDays} days</strong>
                </li>
                <li>
                  Comfortable days (20-25°C):{" "}
                  <strong>{stats.comfortableDays} days</strong>
                </li>
              </>
            ) : (
              <>
                <li>
                  High humidity days (&gt;80%):{" "}
                  <strong>{stats.hotDays} days</strong>
                </li>
                <li>
                  Very high humidity days (&gt;90%):{" "}
                  <strong>{stats.extremelyHotDays} days</strong>
                </li>
                <li>
                  Low humidity days (&lt;30%):{" "}
                  <strong>{stats.coldDays} days</strong>
                </li>
                <li>
                  Very low humidity days (&lt;20%):{" "}
                  <strong>{stats.extremelyColdDays} days</strong>
                </li>
                <li>
                  Comfortable humidity days (40-60%):{" "}
                  <strong>{stats.comfortableDays} days</strong>
                </li>
              </>
            )}
          </ul>
        </div>

        <div className="microclimate-section">
          <h4>Seasonal Analysis</h4>
          <div className="seasons-grid">
            {Object.keys(stats.seasons).map((season) => (
              <div key={season} className="season-card">
                <h5>{season.charAt(0).toUpperCase() + season.slice(1)}</h5>
                <p>
                  Avg. Min:{" "}
                  <strong>
                    {stats.seasons[season].avgMin}
                    {unit}
                  </strong>
                </p>
                <p>
                  Avg. Max:{" "}
                  <strong>
                    {stats.seasons[season].avgMax}
                    {unit}
                  </strong>
                </p>
                <p>
                  Avg. Mean:{" "}
                  <strong>
                    {stats.seasons[season].avgMean}
                    {unit}
                  </strong>
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="microclimate-interpretation">
        <h4>Interpretation</h4>
        <p>
          {isTemperature ? (
            <>
              This location experiences{" "}
              {stats.extremelyHotDays > 30
                ? "a significant number of extremely hot days"
                : stats.extremelyHotDays > 10
                ? "some extremely hot days"
                : "few extremely hot days"}
              , with
              {stats.extremelyColdDays > 30
                ? " frequent freezing temperatures"
                : stats.extremelyColdDays > 10
                ? " occasional freezing temperatures"
                : " rare freezing temperatures"}
              . The average daily temperature swing is{" "}
              {parseFloat(stats.diurnalRange) > 15
                ? "very large"
                : parseFloat(stats.diurnalRange) > 10
                ? "significant"
                : "moderate"}{" "}
              at {stats.diurnalRange}°C.
              {stats.comfortableDays > 100
                ? " This location has many comfortable days throughout the year."
                : stats.comfortableDays > 50
                ? " This location has a moderate number of comfortable days."
                : " This location has relatively few comfortable days."}
            </>
          ) : (
            <>
              This location experiences{" "}
              {stats.hotDays > 100
                ? "high humidity levels for much of the year"
                : stats.hotDays > 50
                ? "periods of high humidity"
                : "generally moderate humidity levels"}
              , with
              {stats.coldDays > 100
                ? " extended periods of low humidity"
                : stats.coldDays > 50
                ? " occasional periods of low humidity"
                : " infrequent periods of low humidity"}
              .
              {stats.comfortableDays > 100
                ? " The humidity levels are comfortable for many days throughout the year."
                : stats.comfortableDays > 50
                ? " The humidity levels are comfortable for a moderate number of days."
                : " The humidity levels are rarely in the comfortable range."}
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default MicroclimateAnalysis;
